/**
 * utilities/vendorScope.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Who a vendor screen works for, and which of the vendor's branches this login
 * may act on. Izi decided on 4 Oct 2026 that a team member can be limited to
 * one or more branches, and that a member with no branch assigned works across
 * the whole business. The server decides every request
 * (services/vendorTeam/branchScope.js); this file mirrors its vocabulary so the
 * dashboard never offers what the server would refuse.
 *
 *   location   "main" for the vendor's own main location, or a 24-hex outlet id
 *   "all"      the dashboard's word for "every location this login may see";
 *              it is never sent to an order book (no outletId means that)
 *
 * Pure: no React, no requests, so the tests and every view can import it.
 * ─────────────────────────────────────────────────────────────────────────────
 */

export const MAIN = "main";
export const ALL = "all";

/** Read keys: a branch member keeps them; the server filters the data. */
export const READ_KEYS = Object.freeze(["catalogue:view", "orders:view"]);

/**
 * Head office only, whatever a branch member's role says (the server's
 * HQ_ONLY_KEYS).
 */
export const HQ_ONLY_KEYS = Object.freeze(["catalogue:edit", "quotes:manage", "team:manage", "settings:edit"]);

/**
 * Keys that act on a product everywhere when no branch is named. A branch
 * member holds them only at a branch (the Branches tab, a branch's sold out
 * switch), never on the product itself, so the product forms and switches
 * lock for them.
 */
export const PRODUCT_LEVEL_KEYS = Object.freeze(["catalogue:activate", "pricing:edit"]);

const HEX24 = /^[0-9a-fA-F]{24}$/;

const asId = (v) => (v == null ? "" : String(v?._id ?? v).trim());

/**
 * The owner's Users id for a signed-in login: a team member works for
 * `parentVendorId`, an owner for their own `_id`. Accepts the stored profile
 * (`{ result }`) or the result itself. Null when nobody is signed in.
 *
 * Every vendor request must name the owner. `user.result._id` alone names the
 * member, and some books and menus came back empty for members because of it.
 */
export const vendorOwnerIdOf = (user) => {
  const me = user?.result || user;
  if (!me || typeof me !== "object") return null;
  const id = asId(me.parentVendorId) || asId(me._id) || asId(me.id);
  return id || null;
};

/**
 * The location key for a value: "main" for "main", null or empty; the lower
 * case hex id for an outlet; null for "all" (no single location).
 */
export const locationKeyOf = (value) => {
  const s = asId(value);
  if (s.toLowerCase() === ALL) return null;
  if (!s || s.toLowerCase() === MAIN) return MAIN;
  return HEX24.test(s) ? s.toLowerCase() : s;
};

/**
 * Canonical branch scope, read the way the server reads it: a missing field
 * or anything other than BRANCHES is the whole business.
 *
 * @returns {{ mode: "ALL"|"BRANCHES", outletIds: string[], includeMain: boolean }}
 */
export const normalizeBranchScope = (raw) => {
  const mode = String(raw?.mode || "").trim().toUpperCase() === "BRANCHES" ? "BRANCHES" : "ALL";
  if (mode === "ALL") return { mode, outletIds: [], includeMain: false };
  const ids = Array.isArray(raw?.outletIds) ? raw.outletIds : [];
  const outletIds = [...new Set(ids.map(asId).filter((id) => HEX24.test(id)).map((id) => id.toLowerCase()))];
  return { mode, outletIds, includeMain: raw?.includeMain === true };
};

/** The location set a branch scope allows; null for the whole business. */
export const scopeSetOf = (branchScope) => {
  const n = normalizeBranchScope(branchScope);
  if (n.mode === "ALL") return null;
  const set = new Set(n.outletIds);
  if (n.includeMain) set.add(MAIN);
  return set;
};

/**
 * The location a permission question names: null when it names none (no
 * location given, or "all"), otherwise its key. Unlike locationKeyOf, an
 * empty value here is not "main": a question with no location is about the
 * product or the business as a whole, which is head office's.
 */
export const targetKeyOf = (location) =>
  location === undefined || location === null || location === "" ? null : locationKeyOf(location);

/**
 * May a login with this scope act at `location`? The whole business may act
 * anywhere; a branch login only inside its set, and never at "all" or at no
 * location.
 */
export const scopeAllows = (scopeSet, location) => {
  if (scopeSet === null || scopeSet === undefined) return true;
  const key = targetKeyOf(location);
  return key !== null && scopeSet.has(key);
};

/**
 * The client half of the server's accessAllows: may a login with `has(key)`
 * and this scope use `key` at `location`?
 *
 *   whole business            has(key)
 *   branch login, no location read keys only (a product-level change is
 *                             head office's)
 *   branch login, a location  never the head office keys; otherwise the
 *                             location must be inside the scope
 */
export const scopedAllows = ({ has, scopeSet }, key, location) => {
  if (key && !has(key)) return false;
  if (scopeSet === null || scopeSet === undefined) return true;
  if (key && HQ_ONLY_KEYS.includes(key)) return false;
  const loc = targetKeyOf(location);
  if (loc === null) return Boolean(key) && READ_KEYS.includes(key);
  return scopeSet.has(loc);
};

/**
 * The order book filter for the active location: undefined for "all" (the
 * server then reads the caller's whole scope), the key otherwise.
 */
export const outletIdParamOf = (active) => {
  const key = locationKeyOf(active);
  return key === null ? undefined : key;
};

/**
 * The location an order is at, for this vendor: a brand order's
 * fulfilmentOutletId, otherwise the first line that names an outlet; "main"
 * when nothing does (every order placed before branches existed).
 */
export const orderLocationKeyOf = (order) => {
  if (!order) return MAIN;
  if (order.isBrandOrder === true) return locationKeyOf(order.fulfilmentOutletId);
  const line = (order.orderItems || []).find((item) => item && item.outletId);
  return locationKeyOf(line ? line.outletId : null);
};

/**
 * The branch name to show for an order: the name snapshotted on the line,
 * the populated outlet's name, the vendor's own name for it, or `mainLabel`.
 */
export const orderLocationName = (order, nameOf = () => null, mainLabel = "Main location") => {
  const key = orderLocationKeyOf(order);
  const line = (order?.orderItems || []).find((item) => item && item.outletId);
  return line?.outletName || line?.outletId?.name || nameOf(key) || (key === MAIN ? mainLabel : "Branch");
};

/** The server's 403 codes for a branch member. */
export const BRANCH_ERROR_CODES = Object.freeze({
  OUT_OF_SCOPE: "BRANCH_OUT_OF_SCOPE",
  HQ_ONLY: "BRANCH_SCOPE_HQ_ONLY",
});

const BRANCH_FALLBACKS = {
  BRANCH_OUT_OF_SCOPE: "Your access does not cover that branch.",
  BRANCH_SCOPE_HQ_ONLY: "Only head office can do this.",
};

/**
 * The message to show for a failed request. The two branch refusals carry
 * their own server message ("Your access covers Ntinda only."), which is
 * shown as it stands; anything else falls back to the body's message, then to
 * `fallback`.
 */
export const branchAwareMessage = (error, fallback = "Something went wrong. Please try again.") => {
  const body = error?.response?.data || {};
  const code = body.code;
  if (code && BRANCH_FALLBACKS[code]) return body.message || BRANCH_FALLBACKS[code];
  return body.message || fallback;
};

/** True when a failed request was a branch scope refusal. */
export const isBranchRefusal = (error) => Boolean(BRANCH_FALLBACKS[error?.response?.data?.code]);

/**
 * How a member's branch access reads in the members table: "All branches",
 * or the branch names (a deleted id reads "Deleted branch").
 *
 * @param {object} branchScope the member's stored scope
 * @param {Array<{ id: string, name: string, isMain?: boolean }>} locations the vendor's locations
 * @param {{ mainLabel?: string }} [opts]
 * @returns {{ all: boolean, names: string[] }}
 */
export const branchScopeNames = (branchScope, locations = [], { mainLabel = "Head office" } = {}) => {
  const n = normalizeBranchScope(branchScope);
  if (n.mode === "ALL") return { all: true, names: [] };
  const byId = new Map((locations || []).map((l) => [locationKeyOf(l.id), l]));
  const names = [];
  if (n.includeMain) names.push(byId.get(MAIN)?.name || mainLabel);
  for (const id of n.outletIds) names.push(byId.get(id)?.name || "Deleted branch");
  return { all: false, names };
};

/**
 * Branch names read as one phrase: "Ntinda", "Ntinda and Kira", "Ntinda,
 * Kira and Mukono", or "{n} branches" past `max` names. Empty when there are
 * none, so a caller can fall back to its own words.
 */
export const joinBranchNames = (names = [], { max = 3 } = {}) => {
  const list = (names || []).filter(Boolean);
  if (!list.length) return "";
  if (list.length > max) return `${list.length} branches`;
  if (list.length === 1) return list[0];
  return `${list.slice(0, -1).join(", ")} and ${list[list.length - 1]}`;
};

/**
 * Validate a branch access choice before it is sent. Returns the error copy
 * or null. The server says the same (400 INVALID_BRANCH_SCOPE).
 */
export const branchScopeError = (value) => {
  const n = normalizeBranchScope(value);
  if (n.mode === "BRANCHES" && !n.outletIds.length && !n.includeMain) {
    return "Choose at least one branch, or give access to the whole business.";
  }
  return null;
};
